import { PF, calibriWorkbook } from './tagFont';
import jsPDF from 'jspdf';
import ExcelJS from 'exceljs';
import { imagePng, ESTIMATE_DISCLAIMER } from '@/lib/tagExcel';
import { purityPctOf } from '@/lib/tagPricing';
import { addSummaryPdf } from '@/lib/tagSummary';
import { fmtSep } from '@/lib/tagMatHandling';

const hexRgb = (h) => { const m = String(h || '#be185d').replace('#', ''); return [0, 2, 4].map((i) => parseInt(m.slice(i, i + 2), 16) || 0); };

const g3 = (n) => Number(n || 0).toFixed(3);
const natural = (a, b) => String(a || '').localeCompare(String(b || ''), undefined, { numeric: true, sensitivity: 'base' });
const NAVY = [30, 42, 74]; const GREEN = [15, 107, 79]; const AMBER = [254, 243, 199]; const SOFT = [243, 245, 249];

// Purity groups (highest purity first), rows by jewellery no.
export function purityGroups(datas) {
  const map = new Map();
  datas.forEach((d) => {
    const label = d.purityText && d.purityText !== '—' ? d.purityText : 'No purity';
    if (!map.has(label)) map.set(label, { label, pct: purityPctOf(d) || 0, items: [] });
    map.get(label).items.push(d);
  });
  return [...map.values()]
    .map((g) => ({ ...g, title: g.label === 'No purity' ? 'No purity' : `${g.label} Gold`, items: g.items.sort((a, b) => natural(a.jewelleryNo, b.jewelleryNo)), gross: g.items.reduce((t, d) => t + Number(d.gross || 0), 0) }))
    .sort((a, b) => b.pct - a.pct || natural(a.label, b.label));
}

// Option A "grouped strips": A4 portrait, list name + column header on every page.
const PW = 210; const PH = 297; const MX = 12; const TOP = 26; const BOT = 285;
const COLS = [{ t: 'PHOTO', x: 0 }, { t: 'JEWELLERY NO.', x: 22 }, { t: 'PURITY', x: 104 }, { t: 'GROSS WT', x: 130 }, { t: 'NET WT', x: 158 }];
const ROW = 17; const IMG = 14; const BAND = 7; const CH = 5.5;

function pageHead(pdf, name, count) {
  pdf.setFont(PF(), 'normal'); pdf.setFontSize(8); pdf.setTextColor(100, 116, 139);
  pdf.text('Item list', MX, 10);
  pdf.setFont(PF(), 'bold'); pdf.setFontSize(15); pdf.setTextColor(...NAVY);
  pdf.text(pdf.splitTextToSize(name || 'Item list', 120)[0], MX, 17);
  pdf.setFont(PF(), 'normal'); pdf.setFontSize(8); pdf.setTextColor(100, 116, 139);
  pdf.text(`${count} piece${count === 1 ? '' : 's'} · ${new Date().toLocaleDateString('en-GB')}`, PW - MX, 17, { align: 'right' });
  pdf.setDrawColor(...NAVY); pdf.setLineWidth(0.6); pdf.line(MX, 20, PW - MX, 20);
}

function colHead(pdf, y) {
  pdf.setFillColor(...SOFT); pdf.rect(MX, y, PW - 2 * MX, CH, 'F');
  pdf.setFont(PF(), 'normal'); pdf.setFontSize(7); pdf.setTextColor(100, 116, 139);
  COLS.forEach((c) => pdf.text(c.t, MX + 3 + c.x, y + 3.8));
  return y + CH;
}

function band(pdf, y, text, fill, color, right) {
  pdf.setFillColor(...fill); pdf.rect(MX, y, PW - 2 * MX, BAND, 'F');
  pdf.setFont(PF(), 'bold'); pdf.setFontSize(9.5); pdf.setTextColor(...color);
  pdf.text(text, MX + 3, y + 4.8);
  if (right) { pdf.setFont(PF(), 'bold'); pdf.text(right, PW - MX - 3, y + 4.8, { align: 'right' }); }
  return y + BAND;
}

export async function buildItemListPdf(datas, name, { summary = false, rmMode = 'expanded' } = {}) {
  const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait', compress: true }); pdf.setFont(PF());
  const groups = purityGroups(datas);
  const imgs = new Map(await Promise.all(datas.map(async (d) => [d.id, await imagePng(d.image)])));
  pageHead(pdf, name, datas.length);
  let y = TOP;
  const room = (h) => y + h <= BOT;
  const newPage = (g) => { pdf.addPage('a4', 'portrait'); pageHead(pdf, name, datas.length); y = TOP; if (g) { y = band(pdf, y, `${g.title} · continued`, NAVY, [255, 255, 255]); y = colHead(pdf, y); } };
  groups.forEach((g) => {
    if (!room(BAND + CH + ROW)) newPage(null);
    y = band(pdf, y, `${g.title} · ${g.items.length} piece${g.items.length === 1 ? '' : 's'}`, NAVY, [255, 255, 255]);
    y = colHead(pdf, y);
    g.items.forEach((d) => {
      if (!room(ROW)) newPage(g);
      const im = imgs.get(d.id);
      const ix = MX + 3; const iy = y + (ROW - IMG) / 2;
      if (im) {
        const s = Math.min(IMG / im.w, IMG / im.h);
        pdf.addImage(im.base64, 'PNG', ix + (IMG - im.w * s) / 2, iy + (IMG - im.h * s) / 2, im.w * s, im.h * s);
      } else {
        pdf.setDrawColor(217, 222, 231); pdf.setLineWidth(0.2); pdf.rect(ix, iy, IMG, IMG, 'S');
        pdf.setFont(PF(), 'italic'); pdf.setFontSize(5.5); pdf.setTextColor(154, 163, 178); pdf.text('No photo', ix + IMG / 2, iy + IMG / 2 + 1, { align: 'center' });
      }
      const ty = y + ROW / 2 + 1.3;
      pdf.setFont(PF(), 'bold'); pdf.setFontSize(9.5); pdf.setTextColor(...GREEN);
      pdf.text(pdf.splitTextToSize(d.jewelleryNo || '', 78)[0], MX + 3 + COLS[1].x, ty);
      pdf.setFillColor(...AMBER); pdf.roundedRect(MX + 2 + COLS[2].x, ty - 3.6, 18, 5, 2.4, 2.4, 'F');
      pdf.setFont(PF(), 'bold'); pdf.setFontSize(7.5); pdf.setTextColor(146, 64, 14);
      pdf.text(pdf.splitTextToSize(d.purityText || '—', 17)[0], MX + 11 + COLS[2].x, ty - 0.2, { align: 'center' });
      pdf.setFont(PF(), 'normal'); pdf.setFontSize(9.5); pdf.setTextColor(...NAVY);
      pdf.text(`${g3(d.gross)} g`, MX + 3 + COLS[3].x, ty);
      pdf.text(`${g3(d.net)} g`, MX + 3 + COLS[4].x, ty);
      // Separate weights (e.g. Dak Wt) just under the net weight, in their colour.
      (d.separate || []).forEach((sp, k) => { pdf.setFont(PF(), 'bold'); pdf.setFontSize(7.5); pdf.setTextColor(...hexRgb(sp.color)); pdf.text(`${sp.label} ${fmtSep(sp)}`, MX + 3 + COLS[4].x, ty + 4 + k * 3.4); });
      pdf.setDrawColor(226, 232, 240); pdf.setLineWidth(0.2); pdf.line(MX, y + ROW, PW - MX, y + ROW);
      y += ROW;
    });
    if (!room(BAND)) newPage(null);
    y = band(pdf, y, `${g.label} subtotal · Gross wt`, AMBER, NAVY, `${g3(g.gross)} g`) + 4;
  });
  if (!room(BAND + 2)) newPage(null);
  band(pdf, y, `Grand total · Gross wt (${datas.length} piece${datas.length === 1 ? '' : 's'})`, GREEN, [255, 255, 255], `${g3(groups.reduce((t, g) => t + g.gross, 0))} g`);
  if (summary) addSummaryPdf(pdf, datas, rmMode, name);
  const n = pdf.getNumberOfPages();
  for (let i = 1; i <= n; i += 1) {
    pdf.setPage(i);
    pdf.setFont(PF(), 'italic'); pdf.setFontSize(7); pdf.setTextColor(148, 163, 184);
    pdf.text(`${ESTIMATE_DISCLAIMER}  ·  Page ${i} of ${n}`, PW / 2, PH - 6, { align: 'center' });
  }
  pdf.setProperties({ title: name || 'Item list' });
  return pdf;
}

// Same layout as Excel: list name in the page header, column header repeated on every printed page.
export async function downloadItemListExcel(datas, name) {
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('Item list', { views: [{ state: 'frozen', ySplit: 3 }] });
  ws.columns = [{ width: 12 }, { width: 34 }, { width: 12 }, { width: 14 }, { width: 14 }];
  const fill = (argb) => ({ type: 'pattern', pattern: 'solid', fgColor: { argb } });
  ws.mergeCells('A1:E1');
  ws.getCell('A1').value = name || 'Item list';
  ws.getCell('A1').font = { bold: true, size: 15, color: { argb: 'FF1E2A4A' } };
  ws.getRow(1).height = 24;
  ws.mergeCells('A2:E2');
  ws.getCell('A2').value = `Item list · ${datas.length} piece${datas.length === 1 ? '' : 's'} · ${new Date().toLocaleDateString('en-GB')}`;
  ws.getCell('A2').font = { size: 9, color: { argb: 'FF64748B' } };
  const hr = ws.getRow(3);
  const SEPS = [...new Map(datas.flatMap((d) => d.separate || []).map((sp) => [sp.label, sp])).values()];
  SEPS.forEach((sp, k) => { ws.getColumn(6 + k).width = 14; });
  ['Photo', 'Jewellery No.', 'Gold Purity', 'Gross Wt (g)', 'Net Wt (g)', ...SEPS.map((sp) => `${sp.label} (${sp.unit})`)].forEach((t, i) => {
    const c = hr.getCell(i + 1); c.value = t; c.font = { bold: true, color: { argb: 'FFFFFFFF' } }; c.fill = fill('FF1E2A4A');
    c.alignment = { vertical: 'middle', horizontal: i > 2 ? 'right' : 'left' };
  });
  hr.height = 20;
  const groups = purityGroups(datas);
  const imgs = new Map(await Promise.all(datas.map(async (d) => [d.id, await imagePng(d.image)])));
  const subs = [];
  groups.forEach((g) => {
    const gh = ws.addRow([`${g.title} · ${g.items.length} piece${g.items.length === 1 ? '' : 's'}`]);
    ws.mergeCells(`A${gh.number}:E${gh.number}`);
    gh.getCell(1).font = { bold: true, color: { argb: 'FFFFFFFF' } }; gh.getCell(1).fill = fill('FF2F3E63');
    const first = ws.rowCount + 1;
    g.items.forEach((d) => {
      const r = ws.addRow(['', d.jewelleryNo, d.purityText || '—', Number(Number(d.gross || 0).toFixed(3)), Number(Number(d.net || 0).toFixed(3)), ...SEPS.map((sp) => (d.separate || []).filter((x) => x.label === sp.label).reduce((t, x) => t + x.qty, 0) || null)]);
      SEPS.forEach((sp, k) => { const c = r.getCell(6 + k); c.numFmt = sp.unit === 'g' ? '0.000' : '0.00'; c.font = { bold: true, color: { argb: `FF${String(sp.color || '#be185d').replace('#', '').toUpperCase()}` } }; });
      r.height = 48;
      r.getCell(2).font = { bold: true, color: { argb: 'FF0F6B4F' } };
      [4, 5].forEach((i) => { r.getCell(i).numFmt = '0.000'; });
      r.eachCell({ includeEmpty: true }, (c) => { c.alignment = { ...(c.alignment || {}), vertical: 'middle' }; c.border = { bottom: { style: 'thin', color: { argb: 'FFE2E8F0' } } }; });
      const im = imgs.get(d.id);
      if (im) {
        const k = Math.min(58 / im.w, 58 / im.h);
        ws.addImage(wb.addImage({ base64: im.base64, extension: 'png' }), { tl: { col: 0.1, row: r.number - 1 + 0.1 }, ext: { width: im.w * k, height: im.h * k }, editAs: 'oneCell' });
      }
    });
    const last = ws.rowCount;
    const sr = ws.addRow(['', `${g.label} subtotal · Gross wt`, '', { formula: `SUM(D${first}:D${last})`, result: Number(g.gross.toFixed(3)) }, '']);
    sr.eachCell({ includeEmpty: true }, (c) => { c.font = { bold: true }; c.fill = fill('FFFEF3C7'); });
    sr.getCell(4).numFmt = '0.000';
    subs.push(`D${sr.number}`);
    ws.addRow([]);
  });
  const tr = ws.addRow(['', `Grand total · Gross wt (${datas.length} piece${datas.length === 1 ? '' : 's'})`, '', { formula: subs.length ? `SUM(${subs.join(',')})` : '0', result: Number(groups.reduce((t, g) => t + g.gross, 0).toFixed(3)) }, '']);
  tr.eachCell({ includeEmpty: true }, (c) => { c.font = { bold: true, color: { argb: 'FFFFFFFF' } }; c.fill = fill('FF0F6B4F'); });
  tr.getCell(4).numFmt = '0.000';
  ws.headerFooter = { oddHeader: `&C&"-,Bold"&12${(name || 'Item list').replace(/&/g, '&&')}`, oddFooter: `&C&8${ESTIMATE_DISCLAIMER}  ·  Page &P of &N` };
  ws.pageSetup = { paperSize: 9, orientation: 'portrait', fitToPage: true, fitToWidth: 1, fitToHeight: 0, printTitlesRow: '3:3' };
  const buf = await calibriWorkbook(wb).xlsx.writeBuffer();
  const url = URL.createObjectURL(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
  const a = document.createElement('a');
  a.href = url; a.download = `${name || 'Item list'} - item list.xlsx`;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}
